// ---- Horizontally scrollable tab strips ----
//
// KEY-462: a tab strip that outgrew its width used to hand the reader a horizontal scrollbar — easy to
// miss, awkward to drag, and on the action-point panel it parked whole tabs behind an edge nobody
// noticed. The strip now hides that scrollbar and grows a chevron button on whichever side still has
// somewhere to go, so the overflow announces itself. With room to spare neither button exists at all.
//
// Two layers. ScrollStrip is the mechanism — measure, buttons, smooth scroll — and knows nothing about
// tabs. TabStrip is the policy: tablist semantics, roving tabindex, arrow keys, and keeping the active
// tab in view. Consumers want TabStrip. Both live in dial-workspace rather than @keydials/ui because
// every consumer is here, they need this package's Icon, and @keydials/ui has no test harness.

import {
  type CSSProperties,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Icon, type IconName } from "./Icon";

// ---------- Layer 1: the scrolling mechanism ----------

/** The three measurements overflow detection needs, named so a test can hand over a plain object —
 *  jsdom lays nothing out, so a real element there reports 0 for all three. */
export interface ScrollMetrics {
  scrollLeft: number;
  clientWidth: number;
  scrollWidth: number;
}

/** Which directions the strip can still travel in. Pure, so it holds up without a layout engine. */
export function scrollAffordances({ scrollLeft, clientWidth, scrollWidth }: ScrollMetrics): {
  canLeft: boolean;
  canRight: boolean;
} {
  return {
    canLeft: scrollLeft > 0,
    // The -1 absorbs the sub-pixel remainder a fractional layout leaves at the far end, which would
    // otherwise leave a button offering to scroll nowhere.
    canRight: scrollLeft + clientWidth < scrollWidth - 1,
  };
}

/** A reader who asked for less motion gets the jump instantly. matchMedia is absent under jsdom. */
function scrollBehaviour(): ScrollBehavior {
  const reduce =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  return reduce ? "auto" : "smooth";
}

const ARROW_BUTTON: CSSProperties = {
  flex: "none",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 26,
  height: 26,
  padding: 0,
  font: "inherit",
  borderRadius: "var(--radius-sm)",
  border: "var(--border-w) solid var(--border)",
  background: "var(--surface)",
  color: "var(--text-muted)",
  cursor: "pointer",
};

export function ScrollStrip({ children, style, scrollerStyle, contentProps, trailing, fadeColour }: {
  children: ReactNode;
  /** The outer row: [button] [scroller] [button] [trailing]. */
  style?: CSSProperties;
  /** The scrolling box, which wears the strip's own chrome — a tray background, an underline. */
  scrollerStyle?: CSSProperties;
  /** Spread onto the flex row that directly holds the children — where role="tablist" belongs. */
  contentProps?: HTMLAttributes<HTMLDivElement>;
  /** Pinned after the strip, outside the scroller, so it never scrolls away — the labels/icons toggle. */
  trailing?: ReactNode;
  /**
   * The colour behind the tabs. When given, a 40px fade into it sits at the right edge while there is
   * more strip to the right, so the cut-off tab reads as "more this way" rather than as clipped.
   */
  fadeColour?: string;
}) {
  const scroller = useRef<HTMLDivElement | null>(null);
  const content = useRef<HTMLDivElement | null>(null);
  const [reach, setReach] = useState({ canLeft: false, canRight: false });

  const measure = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const next = scrollAffordances(el);
    setReach((prev) => (prev.canLeft === next.canLeft && prev.canRight === next.canRight ? prev : next));
  }, []);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    // The strip being resized and its tabs changing width are separate events and either one can turn
    // overflow on or off, so watch both boxes.
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    if (content.current) ro?.observe(content.current);
    return () => {
      el.removeEventListener("scroll", measure);
      ro?.disconnect();
    };
  }, [measure]);

  const nudge = (direction: -1 | 1) => {
    const el = scroller.current;
    if (!el) return;
    // A whole strip-width jump loses the reader's place; 80% leaves one tab behind as an anchor.
    const left = direction * Math.max(1, Math.round(el.clientWidth * 0.8));
    if (typeof el.scrollBy !== "function") {
      el.scrollLeft += left;
      measure();
      return;
    }

    const from = el.scrollLeft;
    el.scrollBy({ left, behavior: scrollBehaviour() });
    measure();
    // Some environments answer behavior:"smooth" by doing nothing at all rather than by jumping — a
    // browser built with scroll animations off, and the automation browser this was checked in. That
    // would leave the button inert, which is worse than the scrollbar it replaced, so if the strip has
    // not moved by the next frame, move it.
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => {
        const now = scroller.current;
        if (!now) return;
        if (now.scrollLeft === from) now.scrollLeft = from + left;
        measure();
      });
    }
  };

  // The buttons are deliberately out of the tab order: keyboard users move with the arrow keys, which
  // already drag the active tab into view, and a focusable button here would be a trap between tabs.
  const arrow = (direction: -1 | 1) => (
    <button
      type="button"
      aria-hidden
      tabIndex={-1}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => nudge(direction)}
      style={ARROW_BUTTON}
    >
      <Icon name={direction === -1 ? "chevLeft" : "chevRight"} size={15} />
    </button>
  );

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0, maxWidth: "100%", ...style }}>
      {reach.canLeft && arrow(-1)}
      {/* The wrapper holds the flex share so the fade can sit over the scroller's edge without scrolling. */}
      <div style={{ position: "relative", display: "flex", minWidth: 0, flex: scrollerStyle?.flex }}>
        <div
          ref={scroller}
          className="kd-noscrollbar"
          style={{
            overflowX: "auto",
            overflowY: "hidden",
            scrollbarWidth: "none",
            minWidth: 0,
            ...scrollerStyle,
            flex: "1 1 auto",
          }}
        >
          <div
            {...contentProps}
            ref={content}
            style={{ display: "flex", alignItems: "center", gap: 4, ...contentProps?.style }}
          >
            {children}
          </div>
        </div>
        {fadeColour && reach.canRight && (
          <div
            aria-hidden
            data-strip-fade
            style={{
              position: "absolute",
              top: 1,
              bottom: 1,
              right: 1,
              width: 40,
              pointerEvents: "none",
              borderTopRightRadius: scrollerStyle?.borderRadius,
              borderBottomRightRadius: scrollerStyle?.borderRadius,
              background: `linear-gradient(to right, transparent, ${fadeColour})`,
            }}
          />
        )}
      </div>
      {reach.canRight && arrow(1)}
      {trailing}
    </div>
  );
}

// ---------- Layer 2: the tab policy ----------

export type TabStripVariant = "underline" | "pill";
/** "md" is the default; "sm" is the compact pill the item card uses one level further in. */
export type TabStripSize = "sm" | "md";

/** Whether a strip shows its tabs' names or their icons — one per-person choice for every strip. */
export type TabStripDisplay = "labels" | "icons";

export interface TabStripItem<Id extends string = string> {
  id: Id;
  label: string;
  /**
   * Drawn in place of the label when the strip shows icons. Chosen by what the tab does, never by its
   * label, because a dial can rename its tabs (KEY-670). Required, so a new tab can't ship without one.
   */
  icon: IconName;
  /** Tally badge; 0 renders no badge — an empty pane is still worth reaching. */
  count?: number;
  /** Amber attention dot: something is waiting behind this tab. */
  alert?: boolean;
  /** Announced and painted as unavailable; clicks and arrow keys pass it by. */
  disabled?: boolean;
  /** "danger" paints the tab red, for a tab whose contents are the problem. */
  tone?: "default" | "danger";
  /** Native tooltip / accessible hint — e.g. why a "danger" tab is red. */
  title?: string;
  /** Rendered as data-help-anchor so a guided tour can point at this tab. */
  helpAnchorId?: string;
}

const PILL = {
  md: { fontSize: 13.5, fontWeight: 700, padding: "8px 18px", activeColor: "var(--text)", trayBorder: true },
  sm: { fontSize: 12.5, fontWeight: 600, padding: "5px 11px", activeColor: "var(--accent-text)", trayBorder: false },
} as const;

const UNDERLINE = {
  md: { fontSize: 13.5, fontWeight: 600, padding: "9px 12px" },
  sm: { fontSize: 12.5, fontWeight: 600, padding: "7px 10px" },
} as const;

const DANGER = "var(--rag-red, #e7161b)";

/** One icon tab's footprint, and the gap between tabs — what decides whether an icon strip fits. */
export const ICON_TAB = { width: 36, height: 32, gap: 4 } as const;

/** The width `count` icon tabs take, tray padding aside: 11 of them is 436px, inside a 484px card. */
export function iconTabsWidth(count: number): number {
  return count <= 0 ? 0 : count * ICON_TAB.width + (count - 1) * ICON_TAB.gap;
}

/**
 * What a tab is called, out loud. The label goes silent when only the icon shows, and a badge's digits
 * would otherwise be read run into the label, so everything the eye gets from the badge and the dot goes
 * into the name.
 */
export function iconTabName(t: Pick<TabStripItem, "label" | "count" | "alert">): string {
  return [t.label, t.count ? String(t.count) : null, t.alert ? "needs attention" : null].filter(Boolean).join(", ");
}

/** The two-way switch at the end of a strip: "Show tabs as" labels or icons. */
function DisplayToggle({ value, onChange }: { value: TabStripDisplay; onChange: (next: TabStripDisplay) => void }) {
  const option = (mode: TabStripDisplay, icon: IconName, name: string) => {
    const on = value === mode;
    return (
      <button
        type="button"
        role="radio"
        aria-checked={on}
        aria-label={name}
        title={name}
        onClick={() => {
          if (!on) onChange(mode);
        }}
        className="kd-focusable"
        style={{
          width: 28,
          height: 28,
          padding: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          border: "none",
          borderRadius: "var(--radius-sm)",
          background: on ? "var(--surface)" : "none",
          boxShadow: on ? "var(--shadow-sm)" : "none",
          color: on ? "var(--text)" : "var(--text-muted)",
          cursor: on ? "default" : "pointer",
        }}
      >
        <Icon name={icon} size={15} />
      </button>
    );
  };
  return (
    <div
      role="radiogroup"
      aria-label="Show tabs as"
      style={{ flex: "none", display: "inline-flex", gap: 2, padding: 2, borderRadius: "var(--radius)", background: "var(--dialbar-bg)" }}
    >
      {option("labels", "textLines", "Labels")}
      {option("icons", "grid", "Icons")}
    </div>
  );
}

export function TabStrip<Id extends string>({
  items,
  active,
  onSelect,
  variant,
  ariaLabel,
  size = "md",
  fill = false,
  style,
  display = "labels",
  onDisplayChange,
}: {
  items: readonly TabStripItem<Id>[];
  active: Id;
  onSelect: (id: Id) => void;
  variant: TabStripVariant;
  /** Accessible name for the tab group, e.g. the item's name. */
  ariaLabel: string;
  size?: TabStripSize;
  /**
   * Spread the tabs evenly across the full width of the strip rather than packing them to the left.
   * For a strip that sits inside a card of its own and reads as that card's own navigation (the item
   * card, KEY-485), where a left-packed tray looks like a stray control dropped on the body.
   */
  fill?: boolean;
  /** Outer wrapper styles — margins and, for a pill strip in a flex column, alignSelf. */
  style?: CSSProperties;
  /** Names or icons. Defaults to names. */
  display?: TabStripDisplay;
  /** When given, the strip ends in a labels/icons toggle that reports the reader's pick here. */
  onDisplayChange?: (next: TabStripDisplay) => void;
}) {
  const buttons = useRef(new Map<Id, HTMLButtonElement | null>());

  useEffect(() => {
    const bring = () => {
      const el = buttons.current.get(active);
      // A strip with room to spare is a no-op here; jsdom has no scrollIntoView at all.
      if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ inline: "nearest", block: "nearest" });
    };
    bring();
    // Travelling along the strip is often what makes a chevron appear, and a chevron appearing takes
    // ~30px off the strip — enough to shove the tab just arrived at straight back out of sight. Ask
    // again once the buttons have settled; it is a no-op if the tab is already in view.
    if (typeof requestAnimationFrame !== "function") return;
    const frame = requestAnimationFrame(bring);
    return () => cancelAnimationFrame(frame);
  }, [active]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight" && e.key !== "Home" && e.key !== "End") return;
    const reachable = items.filter((i) => !i.disabled);
    if (reachable.length === 0) return;
    e.preventDefault();

    const at = reachable.findIndex((i) => i.id === active);
    let next: TabStripItem<Id>;
    if (e.key === "Home" || at < 0) next = reachable[0];
    else if (e.key === "End") next = reachable[reachable.length - 1];
    else {
      const step = e.key === "ArrowRight" ? 1 : -1;
      next = reachable[(at + step + reachable.length) % reachable.length];
    }

    if (next.id !== active) onSelect(next.id);
    buttons.current.get(next.id)?.focus();
  };

  // Exactly one tab holds the tab stop. If the active id isn't among the selectable tabs the first one
  // that is takes it, so the strip is never unreachable by keyboard.
  const roving = items.some((i) => i.id === active && !i.disabled) ? active : items.find((i) => !i.disabled)?.id;

  const pill = PILL[size];
  const underline = UNDERLINE[size];
  const scrollerStyle: CSSProperties = variant === "pill"
    ? {
      flex: fill ? "1 1 auto" : "0 1 auto",
      padding: 4,
      background: "var(--surface-inset)",
      borderRadius: "var(--radius)",
      ...(pill.trayBorder ? { border: "var(--border-w) solid var(--border)" } : null),
    }
    : { flex: "1 1 auto", borderBottom: "var(--border-w) solid var(--border)" };

  const icons = display === "icons";

  return (
    <ScrollStrip
      style={style}
      scrollerStyle={scrollerStyle}
      contentProps={{ role: "tablist", "aria-label": ariaLabel, "aria-orientation": "horizontal", onKeyDown }}
      fadeColour={variant === "pill" ? "var(--surface-inset)" : "var(--surface)"}
      trailing={onDisplayChange ? <DisplayToggle value={display} onChange={onDisplayChange} /> : undefined}
    >
      {items.map((t) => {
        const on = t.id === active;
        const danger = t.tone === "danger";
        // An icon tab is a fixed square, whatever the strip's size or fill: its whole point is to be small.
        const iconBox: CSSProperties | null = icons
          ? { width: ICON_TAB.width, height: ICON_TAB.height, padding: 0, justifyContent: "center", flex: "none" }
          : null;
        const base: CSSProperties = {
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          font: "inherit",
          cursor: t.disabled ? "not-allowed" : "pointer",
          whiteSpace: "nowrap",
          // A filling tab takes an equal share of the row and centres its own label; `0` as the basis is
          // what makes the shares equal rather than proportional to each label's length.
          flex: fill ? "1 1 0" : "none",
          justifyContent: fill ? "center" : undefined,
          opacity: t.disabled ? 0.55 : 1,
        };
        const skin: CSSProperties = variant === "pill"
          ? {
            border: "none",
            fontSize: pill.fontSize,
            fontWeight: pill.fontWeight,
            padding: pill.padding,
            borderRadius: "var(--radius-sm)",
            background: on ? "var(--surface)" : "none",
            color: danger ? DANGER : on ? pill.activeColor : "var(--text-muted)",
            boxShadow: on ? "var(--shadow-sm)" : "none",
          }
          : {
            fontSize: underline.fontSize,
            fontWeight: underline.fontWeight,
            padding: underline.padding,
            border: "none",
            background: "none",
            color: danger ? DANGER : on ? "var(--accent-text)" : "var(--text-muted)",
            borderBottom: `2px solid ${on ? (danger ? DANGER : "var(--accent)") : "transparent"}`,
            marginBottom: -1,
          };

        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={on}
            aria-disabled={t.disabled || undefined}
            // Named out loud as label, count, alert in both modes: the badge's number is otherwise read run
            // together with the label ("Files2"), and in icon mode there is no label to read at all.
            aria-label={iconTabName(t)}
            tabIndex={t.id === roving ? 0 : -1}
            title={icons ? (t.title ? `${t.label} — ${t.title}` : t.label) : t.title}
            data-help-anchor={t.helpAnchorId}
            ref={(el) => {
              buttons.current.set(t.id, el);
            }}
            onClick={() => {
              if (!t.disabled) onSelect(t.id);
            }}
            className="kd-focusable"
            style={{ ...base, ...skin, ...iconBox }}
          >
            {icons ? <Icon name={t.icon} size={18} /> : t.label}
            {!!t.count && icons && (
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  // Up into the tray's padding on a pill strip; an underline strip has none to spare, and
                  // its scroller clips anything that pokes above it.
                  top: variant === "pill" ? -4 : 0,
                  right: variant === "pill" ? -6 : -2,
                  minWidth: 15,
                  height: 15,
                  padding: "0 4px",
                  borderRadius: 99,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  fontWeight: 700,
                  background: "var(--text)",
                  color: "var(--surface)",
                }}
              >
                {t.count}
              </span>
            )}
            {!!t.count && !icons && (
              <span
                style={{
                  minWidth: 17,
                  height: 17,
                  padding: "0 5px",
                  borderRadius: 99,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10.5,
                  fontWeight: 700,
                  background: on ? "var(--accent-soft)" : "var(--bg-accent, var(--border))",
                  color: on ? "var(--accent-text)" : "var(--text-muted)",
                }}
              >
                {t.count}
              </span>
            )}
            {t.alert && (
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  top: icons ? 5 : 3,
                  right: icons ? 6 : 3,
                  width: 7,
                  height: 7,
                  borderRadius: 99,
                  background: "var(--st-amber, #b45309)",
                  // Ringed in whatever sits behind it, so the dot stays a dot on top of an icon's strokes.
                  boxShadow: icons ? `0 0 0 2px ${on ? "var(--surface)" : "var(--surface-inset)"}` : undefined,
                }}
              />
            )}
          </button>
        );
      })}
    </ScrollStrip>
  );
}
